import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Store, Video, MessageSquare, CheckCircle, Clock, Phone, Mail, Edit } from 'lucide-react';
import { toast } from 'sonner';
import VideoCall from '../components/VideoCall';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';
import BuyerEventsTab from '../components/buyer/BuyerEventsTab';
import BuyerExhibitorsTab from '../components/buyer/BuyerExhibitorsTab';
import BuyerMessagesTab from '../components/buyer/BuyerMessagesTab';
import BuyerInteractionsTab from '../components/buyer/BuyerInteractionsTab';
import BuyerRegisterModal from '../components/buyer/BuyerRegisterModal';
import BuyerBoothDetailModal from '../components/buyer/BuyerBoothDetailModal';
import BuyerInteractionRequestModal from '../components/buyer/BuyerInteractionRequestModal';
import BuyerPaymentModal from '../components/buyer/BuyerPaymentModal';
import BuyerMessageModal from '../components/buyer/BuyerMessageModal';
import BuyerListPurchaseModal from '../components/buyer/BuyerListPurchaseModal';
import BuyerProfileModal from '../components/buyer/BuyerProfileModal';

const BuyerDashboard = () => {
  const { user } = useContext(AuthContext);
  const { formatPrice, getCurrencyCode, getConvertedAmount } = useCurrency();
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language || 'en').split('@')[0].split('-')[0];

  // Core data
  const [events, setEvents] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [exhibitors, setExhibitors] = useState([]);
  const [myInteractions, setMyInteractions] = useState([]);
  const [activeTab, setActiveTab] = useState('events');
  const [searchTerm, setSearchTerm] = useState('');
  const [notificationCounts, setNotificationCounts] = useState({ messages: 0, queue: 0, requests: 0 });

  // Modal states
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showBoothModal, setShowBoothModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showListPurchaseModal, setShowListPurchaseModal] = useState(false);

  // Form & selection states
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [boothDetailTab, setBoothDetailTab] = useState('overview');
  const [interactionType, setInteractionType] = useState('call_request');
  const [interactionMessage, setInteractionMessage] = useState('');
  const [eventToRegister, setEventToRegister] = useState(null);
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const [activeVideoRoom, setActiveVideoRoom] = useState(null);
  const [messageToExhibitor, setMessageToExhibitor] = useState('');
  const [messageBooth, setMessageBooth] = useState(null);
  const [purchasingEventId, setPurchasingEventId] = useState(null);
  const [listPricing, setListPricing] = useState({});

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  // Profile form
  const [profileForm, setProfileForm] = useState({
    name: '', company: '', phone: '', company_email: '', website: '',
    street_address: '', city: '', state_province: '', postal_code: '', country: '', list_visible: true
  });

  useEffect(() => { fetchData(); }, []);

  // Poll for interaction updates using a ref to avoid dependency issues
  const interactionsRef = useRef(myInteractions);
  useEffect(() => { interactionsRef.current = myInteractions; }, [myInteractions]);

  useEffect(() => {
    let intervalId = null;
    
    const pollInteractions = async () => {
      try {
        const res = await axios.get(`${API}/interactions/buyer`).catch(() => ({ data: [] }));
        const newInteractions = res.data;
        const oldInteractions = interactionsRef.current;
        
        // Check for status transitions and notify
        newInteractions.forEach(newInt => {
          const oldInt = oldInteractions.find(i => i.id === newInt.id);
          if (oldInt && oldInt.status === 'in_queue' && newInt.status === 'on_call') {
            toast.success('Your video call is ready! Click "Join Call" to connect.', { duration: 10000 });
          }
        });
        setMyInteractions(newInteractions);
      } catch (error) { console.error('Poll error:', error); }
    };

    // Always poll every 5s when buyer has any pending or active call interactions
    const hasActiveCalls = myInteractions.some(i => 
      i.interaction_type === 'call_request' && 
      (i.status === 'in_queue' || i.status === 'on_call')
    );
    
    if (hasActiveCalls) {
      intervalId = setInterval(pollInteractions, 5000);
    }
    
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [myInteractions.length, myInteractions.map(i => `${i.id}:${i.status}`).join(',')]);

  const fetchData = async () => {
    try {
      const [eventsRes, registrationsRes, interactionsRes, unreadRes, countsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/registrations`).catch(() => ({ data: [] })),
        axios.get(`${API}/interactions/buyer`).catch(() => ({ data: [] })),
        axios.get(`${API}/messages/unread-count`).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API}/notifications/counts`).catch(() => ({ data: { messages: 0, queue: 0, requests: 0 } }))
      ]);
      setEvents(eventsRes.data.filter(e => e.status === 'upcoming' || e.status === 'registration_open' || e.status === 'live'));
      const userRegs = registrationsRes.data.filter(r => r.user_id === user?.id);
      setMyRegistrations(userRegs);
      setMyInteractions(interactionsRes.data);
      setNotificationCounts(countsRes.data);
      if (userRegs.length > 0) {
        const eventId = userRegs[0].event_id;
        const event = eventsRes.data.find(e => e.id === eventId);
        setSelectedEvent(event);
        await loadExhibitors(eventId);
      }
    } catch (error) { console.error('Failed to fetch data', error); toast.error('Failed to load dashboard'); }
  };

  const loadExhibitors = async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/exhibitors`);
      setExhibitors(res.data);
      const pricingRes = await axios.get(`${API}/events/${eventId}/list-pricing`).catch(() => ({ data: null }));
      if (pricingRes.data) setListPricing(prev => ({ ...prev, [eventId]: pricingRes.data }));
    } catch (error) { console.error('Failed to load exhibitors', error); }
  };

  // Exhibitor list purchase
  const handlePurchaseExhibitorList = (eventId) => { setPurchasingEventId(eventId); setShowListPurchaseModal(true); };
  const createExhibitorListOrder = async () => {
    try {
      const usdPrice = listPricing[purchasingEventId]?.exhibitor_list_price || 0;
      const convertedAmount = getConvertedAmount(usdPrice);
      const response = await axios.post(`${API}/payments/create-list-order`, { event_id: purchasingEventId, list_type: 'exhibitor_list', currency_code: getCurrencyCode(), amount: convertedAmount });
      return response.data.order_id;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create order'); throw e; }
  };
  const captureExhibitorListOrder = async (orderId) => {
    try {
      const response = await axios.post(`${API}/payments/capture-list-order/${orderId}`);
      if (response.data.status === 'success') {
        toast.success('Exhibitor list purchased successfully!');
        setShowListPurchaseModal(false); setPurchasingEventId(null);
        const pricingRes = await axios.get(`${API}/events/${purchasingEventId}/list-pricing`);
        setListPricing(prev => ({ ...prev, [purchasingEventId]: pricingRes.data }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment capture failed'); }
  };
  const downloadExhibitorList = async (eventId, format = 'csv') => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/download/exhibitor-list?format=${format}`, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = downloadUrl; link.download = `exhibitors_${eventId}.${format}`;
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(downloadUrl);
      toast.success('Exhibitor list downloaded successfully!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to download exhibitor list'); }
  };

  // Event registration & payment
  const registerForEvent = async () => {
    if (!eventToRegister) return;
    const originalFee = eventToRegister.buyer_fee || 0;
    const finalFee = getDiscountedAmount(originalFee);
    if (finalFee > 0) {
      setPendingRegistration({ isNew: true, event: eventToRegister, event_id: eventToRegister.id, coupon: couponApplied, originalAmount: originalFee, finalAmount: finalFee });
      setShowRegisterModal(false); setShowPaymentModal(true);
      toast.info('Please complete payment to register for this exhibit.');
    } else {
      try {
        await axios.post(`${API}/registrations`, { event_id: eventToRegister.id, coupon_code: couponApplied?.code || null });
        toast.success('Registered successfully! You can now browse exhibitors.');
        setShowRegisterModal(false); setEventToRegister(null); resetCouponState(); fetchData();
      } catch (error) { toast.error(error.response?.data?.detail || 'Failed to register'); }
    }
  };
  const handlePaymentSuccess = async () => { toast.success('Payment successful! You can now browse exhibitors.'); setShowPaymentModal(false); setPendingRegistration(null); setEventToRegister(null); fetchData(); };
  const handlePaymentError = () => { toast.error('Payment failed. Please try again.'); setShowPaymentModal(false); };
  const handlePaymentCancel = () => { toast.warning('Payment cancelled. No registration was created.'); setShowPaymentModal(false); setPendingRegistration(null); setEventToRegister(null); setCouponCode(''); setCouponApplied(null); setCouponError(''); };

  // Coupon
  const validateCoupon = async () => {
    if (!couponCode.trim() || !eventToRegister) return;
    setCouponLoading(true); setCouponError('');
    try {
      const response = await axios.post(`${API}/events/${eventToRegister.id}/validate-coupon`, { code: couponCode.trim(), user_type: 'attendee' });
      setCouponApplied({ ...response.data, code: couponCode.trim() }); toast.success(`Coupon applied! ${response.data.discount_percent}% discount`);
    } catch (error) { setCouponError(error.response?.data?.detail || 'Invalid coupon code'); setCouponApplied(null); }
    finally { setCouponLoading(false); }
  };
  const getDiscountedAmount = (originalAmount) => { if (!couponApplied) return originalAmount; return Math.max(0, originalAmount - originalAmount * (couponApplied.discount_percent / 100)); };
  const resetCouponState = () => { setCouponCode(''); setCouponApplied(null); setCouponError(''); };

  // Video calls
  const joinVideoCall = async (interaction) => {
    try {
      toast.loading('Joining call...');
      const response = await axios.get(`${API}/video/join/${interaction.id}`);
      toast.dismiss();
      const boothEvent = events.find(e => e.id === interaction.booth?.event_id);
      setActiveVideoRoom({ roomUrl: response.data.room_url, token: response.data.token, interactionId: response.data.interaction_id, exhibitorName: interaction.booth?.company_name || 'Exhibitor', repName: interaction.rep_name || interaction.rep?.name, exhibitName: boothEvent?.name });
      setShowVideoCall(true);
      try { await axios.post(`${API}/video/buyer-joined/${response.data.interaction_id}`); } catch (e) { console.warn('Failed to report buyer join:', e); }
    } catch (error) { toast.dismiss(); toast.error(error.response?.data?.detail || 'Failed to join call'); }
  };
  const endVideoCall = async () => {
    if (!activeVideoRoom?.interactionId) return;
    try { await axios.post(`${API}/video/end/${activeVideoRoom.interactionId}`); setShowVideoCall(false); setActiveVideoRoom(null); toast.success('Call ended'); fetchData(); }
    catch (error) { console.error('Failed to end call:', error); setShowVideoCall(false); setActiveVideoRoom(null); }
  };

  // Interactions
  const createInteraction = async () => {
    if (!selectedBooth || !selectedEvent) return;
    try {
      await axios.post(`${API}/interactions`, { booth_id: selectedBooth.id, event_id: selectedEvent.id, interaction_type: interactionType, message: interactionMessage });
      toast.success(interactionType === 'call_request' ? 'You have been added to the queue. The exhibitor will connect with you shortly.' : 'Your documentation request has been sent. The exhibitor will respond via email.');
      setShowInteractionModal(false); setInteractionMessage(''); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create request'); }
  };

  // Messaging
  const startConversation = async () => {
    if (!messageBooth || !selectedEvent) return;
    try {
      await axios.post(`${API}/messages/conversations`, { booth_id: messageBooth.id, event_id: selectedEvent.id, initial_message: messageToExhibitor.trim() || null });
      toast.success('Message sent! Check your Messages tab to continue the conversation.');
      setShowMessageModal(false); setMessageToExhibitor(''); setMessageBooth(null); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to send message'); }
  };

  // Profile
  const openProfileModal = () => {
    const info = user?.company_info || {};
    setProfileForm({ name: user?.name || '', company: user?.company || info.company_name || '', phone: info.phone || '', company_email: info.company_email || '', website: info.website || '', street_address: info.street_address || '', city: info.city || '', state_province: info.state_province || '', postal_code: info.postal_code || '', country: info.country || '', list_visible: user?.list_visible !== false });
    setShowProfileModal(true);
  };

  // Status badge helper
  const getStatusBadge = (status) => {
    const configs = {
      pending: { color: 'bg-slate-100 text-slate-700', icon: Clock },
      in_queue: { color: 'bg-blue-100 text-blue-700', icon: Clock },
      on_call: { color: 'bg-green-100 text-green-700', icon: Video },
      email_sent: { color: 'bg-yellow-100 text-yellow-700', icon: Mail },
      email_responded: { color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
      completed: { color: 'bg-slate-100 text-slate-700', icon: CheckCircle }
    };
    const statusLabels = {
      pending: t('dashboard.status_pending'), in_queue: t('dashboard.status_in_queue'), on_call: t('dashboard.status_on_call'),
      email_sent: t('dashboard.status_email_sent'), email_responded: t('dashboard.status_email_responded'),
      completed: t('dashboard.status_completed'), abandoned: t('dashboard.status_abandoned')
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    return <Badge className={config.color}><Icon className="h-3 w-3 mr-1" />{statusLabels[status] || status?.replace('_', ' ')}</Badge>;
  };

  const filteredExhibitors = exhibitors.filter(e =>
    e.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCall = myInteractions.find(i => i.interaction_type === 'call_request' && i.status === 'on_call' && i.room_url);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="buyer-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Active Call Banner */}
        {activeCall && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-3">
              <div className="bg-green-500 rounded-full p-2"><Video className="h-6 w-6 text-white" /></div>
              <div>
                <p className="font-semibold text-green-800">{t('dashboard.video_call_ready_title')}</p>
                <p className="text-sm text-green-600">{t('dashboard.waiting_for_you', { company: activeCall.booth?.company_name || 'Exhibitor' })}</p>
              </div>
            </div>
            <Button onClick={() => joinVideoCall({...activeCall, booth: activeCall.booth || exhibitors.find(e => e.id === activeCall.booth_id)})} className="bg-green-600 hover:bg-green-700">
              <Phone className="h-4 w-4 mr-2" /> {t('dashboard.join_call_now')}
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1">{t('dashboard.attendee_dashboard')}</h1>
            <p className="text-slate-600">{t('dashboard.welcome')}, {user?.name}</p>
          </div>
          <Button variant="outline" onClick={openProfileModal}>
            <Edit className="mr-2 h-4 w-4" /> {t('dashboard.edit_profile')}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card><CardContent className="pt-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600">{t('dashboard.registered_events')}</p><p className="text-2xl font-bold">{myRegistrations.length}</p></div><Calendar className="h-8 w-8 text-primary" /></div></CardContent></Card>
          <Card><CardContent className="pt-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600">{t('dashboard.available_exhibitors')}</p><p className="text-2xl font-bold">{exhibitors.length}</p></div><Store className="h-8 w-8 text-accent" /></div></CardContent></Card>
          <Card><CardContent className="pt-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600">{t('dashboard.my_interactions')}</p><p className="text-2xl font-bold">{myInteractions.length}</p></div><MessageSquare className="h-8 w-8 text-green-600" /></div></CardContent></Card>
          <Card><CardContent className="pt-4"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-600">{t('dashboard.in_queue')}</p><p className="text-2xl font-bold">{myInteractions.filter(i => i.status === 'in_queue').length}</p></div><Clock className="h-8 w-8 text-blue-600" /></div></CardContent></Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 max-w-lg">
            <TabsTrigger value="events">{t('dashboard.events')}</TabsTrigger>
            <TabsTrigger value="exhibitors">{t('dashboard.exhibitors')}</TabsTrigger>
            <TabsTrigger value="messages" className="relative">
              {t('dashboard.messages')}
              {notificationCounts.messages > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{notificationCounts.messages > 9 ? '9+' : notificationCounts.messages}</span>}
            </TabsTrigger>
            <TabsTrigger value="interactions" className="relative">
              {t('dashboard.video_conference')}
              {notificationCounts.requests > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{notificationCounts.requests > 9 ? '9+' : notificationCounts.requests}</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <BuyerEventsTab events={events} myRegistrations={myRegistrations} currentLang={currentLang} formatPrice={formatPrice} t={t}
              setSelectedEvent={setSelectedEvent} loadExhibitors={loadExhibitors} setActiveTab={setActiveTab} setEventToRegister={setEventToRegister} setShowRegisterModal={setShowRegisterModal} />
          </TabsContent>
          <TabsContent value="exhibitors">
            <BuyerExhibitorsTab selectedEvent={selectedEvent} myRegistrations={myRegistrations} filteredExhibitors={filteredExhibitors}
              searchTerm={searchTerm} setSearchTerm={setSearchTerm} listPricing={listPricing} downloadExhibitorList={downloadExhibitorList}
              handlePurchaseExhibitorList={handlePurchaseExhibitorList} setSelectedBooth={setSelectedBooth} setShowBoothModal={setShowBoothModal}
              setInteractionType={setInteractionType} setShowInteractionModal={setShowInteractionModal} setMessageBooth={setMessageBooth}
              setShowMessageModal={setShowMessageModal} setActiveTab={setActiveTab} formatPrice={formatPrice} t={t} events={events} />
          </TabsContent>
          <TabsContent value="messages">
            <BuyerMessagesTab myRegistrations={myRegistrations} setActiveTab={setActiveTab} t={t} />
          </TabsContent>
          <TabsContent value="interactions">
            <BuyerInteractionsTab myInteractions={myInteractions} events={events} exhibitors={exhibitors} getStatusBadge={getStatusBadge} joinVideoCall={joinVideoCall} t={t} />
          </TabsContent>
        </Tabs>

        {/* Modals */}
        <BuyerRegisterModal open={showRegisterModal} onOpenChange={setShowRegisterModal} eventToRegister={eventToRegister}
          currentLang={currentLang} formatPrice={formatPrice} t={t} couponCode={couponCode} setCouponCode={setCouponCode}
          couponApplied={couponApplied} setCouponApplied={setCouponApplied} couponLoading={couponLoading}
          couponError={couponError} setCouponError={setCouponError} validateCoupon={validateCoupon}
          getDiscountedAmount={getDiscountedAmount} resetCouponState={resetCouponState} registerForEvent={registerForEvent} />

        <BuyerBoothDetailModal open={showBoothModal} onOpenChange={setShowBoothModal} selectedBooth={selectedBooth}
          boothDetailTab={boothDetailTab} setBoothDetailTab={setBoothDetailTab} formatPrice={formatPrice} t={t}
          onTalkToRep={() => { setShowBoothModal(false); setInteractionType('call_request'); setShowInteractionModal(true); }} />

        <BuyerInteractionRequestModal open={showInteractionModal} onOpenChange={setShowInteractionModal}
          interactionType={interactionType} interactionMessage={interactionMessage} setInteractionMessage={setInteractionMessage}
          selectedBooth={selectedBooth} user={user} createInteraction={createInteraction} t={t} />

        <BuyerPaymentModal open={showPaymentModal} pendingRegistration={pendingRegistration} formatPrice={formatPrice} t={t}
          handlePaymentSuccess={handlePaymentSuccess} handlePaymentError={handlePaymentError} handlePaymentCancel={handlePaymentCancel} />

        <BuyerMessageModal open={showMessageModal} onOpenChange={setShowMessageModal} messageBooth={messageBooth}
          messageToExhibitor={messageToExhibitor} setMessageToExhibitor={setMessageToExhibitor}
          user={user} startConversation={startConversation} t={t} />

        <BuyerListPurchaseModal open={showListPurchaseModal} onOpenChange={setShowListPurchaseModal}
          purchasingEventId={purchasingEventId} listPricing={listPricing} formatPrice={formatPrice}
          createExhibitorListOrder={createExhibitorListOrder} captureExhibitorListOrder={captureExhibitorListOrder} t={t} />

        <BuyerProfileModal open={showProfileModal} onOpenChange={setShowProfileModal}
          profileForm={profileForm} setProfileForm={setProfileForm} user={user} t={t} />

        {/* Video Call */}
        {showVideoCall && activeVideoRoom && (
          <VideoCall key={activeVideoRoom.interactionId} roomUrl={activeVideoRoom.roomUrl} token={activeVideoRoom.token} userName={user?.name || 'Attendee'}
            onCallEnd={endVideoCall} onError={(error) => { console.error('Video call error:', error); toast.error('Video call error'); }} />
        )}
      </div>
    </div>
  );
};

export default BuyerDashboard;
